import React from 'react';
const BLUE = '#2C5FB8';
const RED = '#C8323A';
type Station = {
  x: number;
  y: number;
  name: string;
  job: string;
  fill: string;
  stroke: string;
};
const STATIONS: Station[] = [{
  x: 160,
  y: 104,
  name: 'BAT',
  job: 'batting',
  fill: '#F6E6BF',
  stroke: '#C99632'
}, {
  x: 258,
  y: 248,
  name: 'GLOVE',
  job: 'fielding',
  fill: '#DCEEF9',
  stroke: '#3E8FC7'
}, {
  x: 62,
  y: 248,
  name: 'BALL',
  job: 'bowling',
  fill: '#F8DCDC',
  stroke: '#C8323A'
}];

/** Player positions around a station (offsets). */
const PLAYERS: [number, number, string][] = [[-24, -30, BLUE], [0, -40, RED], [24, -30, BLUE], [-30, 26, RED], [30, 26, BLUE]];
const CENTER: [number, number] = [160, 200];

/** Curved arrow from one station to the next, bending away from the centre. */
function rotationArrow(a: Station, b: Station) {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const cx = CENTER[0];
  const cy = CENTER[1];
  const dx = mx - cx;
  const dy = my - cy;
  const len = Math.hypot(dx, dy) || 1;
  const bend = 26;
  const qx = mx + dx / len * bend;
  const qy = my + dy / len * bend;
  // trim the ends so the arrow starts and stops outside the station circles
  const trim = (px: number, py: number, tx: number, ty: number, r: number) => {
    const vx = tx - px;
    const vy = ty - py;
    const l = Math.hypot(vx, vy) || 1;
    return [px + vx / l * r, py + vy / l * r];
  };
  const [sx, sy] = trim(a.x, a.y, qx, qy, 54);
  const [ex, ey] = trim(b.x, b.y, qx, qy, 58);
  return `M${sx.toFixed(1)} ${sy.toFixed(1)} Q${qx.toFixed(1)} ${qy.toFixed(1)} ${ex.toFixed(1)} ${ey.toFixed(1)}`;
}
export function StationTriangle() {
  return <div className="flex h-full flex-col rounded-3xl bg-cream-light p-5 text-left text-ink shadow-page sm:p-7" data-id="element-326" data-mp="p1flfjcxuy41">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-grass-deep" data-id="element-327" data-mp="u2wjl741g6gx">Space</p>
      <h3 className="font-display text-xl font-extrabold leading-tight sm:text-2xl" data-id="element-328" data-mp="39u0ylltejalp">The Station Triangle</h3>
      <p className="mt-1 text-[13px] leading-snug text-ink-soft" data-id="element-329" data-mp="3eoodcsylsqn3">
        Three groups, three stations, one rotation signal. Nobody waits for a turn.
      </p>

      <svg viewBox="0 0 320 330" className="mx-auto mt-3 w-full max-w-[340px]" role="img" aria-label="Three stations in a triangle: bat station for batting, ball station for bowling and glove station for fielding. Groups rotate clockwise on one signal." data-id="element-330" data-mp="1fd0t9o8z9dxy">
        <defs data-id="element-331" data-mp="11k1hnxcbg3vo">
          <marker id="st-arrow" viewBox="0 0 8 8" refX="4" refY="4" markerWidth="5" markerHeight="5" orient="auto-start-reverse" data-id="element-332" data-mp="11kkzle1yus9p">
            <path d="M0 0 L8 4 L0 8 Z" fill="#0F1B3D" data-id="element-333" data-mp="wjn7a8n8shuh" />
          </marker>
        </defs>

        {/* rotation arrows */}
        {STATIONS.map((s, i) => <path key={s.name} d={rotationArrow(s, STATIONS[(i + 1) % 3])} stroke="#0F1B3D" strokeWidth="2.4" strokeDasharray="5 4" fill="none" markerEnd="url(#st-arrow)" data-id="element-334" data-mp="nl1rumd2hixx" data-mpi={i} />)}

        {/* centre: one signal */}
        <g data-id="element-335" data-mp="3rlcm8h6b4fe">
          <circle cx={CENTER[0]} cy={CENTER[1]} r="24" fill="#0F1B3D" data-id="element-336" data-mp="3pqsgwdq7gcd" />
          <text x={CENTER[0]} y={CENTER[1] - 3} textAnchor="middle" fill="#F5F0DC" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="9" letterSpacing=".6" data-id="element-337" data-mp="8pd2yy266ihh">
            ONE
          </text>
          <text x={CENTER[0]} y={CENTER[1] + 8} textAnchor="middle" fill="#F5F0DC" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="9" letterSpacing=".6" data-id="element-338" data-mp="hhygnzqy1yzg">
            SIGNAL
          </text>
        </g>

        {STATIONS.map(s => <g key={s.name} data-id="element-339" data-mp="3lwyb7qre85q9">
            <circle cx={s.x} cy={s.y} r="44" fill={s.fill} stroke={s.stroke} strokeWidth="2.5" data-id="element-340" data-mp="3ly9frssnxhif" />
            {PLAYERS.map(([dx, dy, color], i) => <circle key={i} cx={s.x + dx * 0.62} cy={s.y + dy * 0.62} r="6.2" fill={color} stroke="#FFFFFF" strokeWidth="1.6" data-id="element-341" data-mp="3uy5wn88t9c2x" data-mpi={i} />)}
            {/* cone */}
            <path d={`M${s.x} ${s.y - 7} L${s.x + 6} ${s.y + 5} L${s.x - 6} ${s.y + 5} Z`} fill="#F07F2A" data-id="element-342" data-mp="1a6lzloo80c9u" />
            <text x={s.x} y={s.name === 'BAT' ? s.y - 70 : s.y + 62} textAnchor="middle" fill="#0F1B3D" fontFamily="'Inter Tight', Inter, sans-serif" fontWeight="800" fontSize="13" letterSpacing=".4" data-id="element-343" data-mp="18p6ruec1duhs">
              {s.name} STATION
            </text>
            <text x={s.x} y={s.name === 'BAT' ? s.y - 56 : s.y + 76} textAnchor="middle" fill="#47526E" fontFamily="Inter, sans-serif" fontWeight="500" fontSize="11" data-id="element-344" data-mp="1hrpw92vh0f65">
              {s.job}
            </text>
          </g>)}
      </svg>

      <ul className="mt-auto flex flex-wrap gap-x-4 gap-y-1.5 border-t border-cream-dark pt-3 text-[12px] text-ink-soft" data-id="element-345" data-mp="1mprfbnswcehm">
        <li className="flex items-center gap-1.5" data-id="element-346" data-mp="21dcdd3awznio"><span className="h-2.5 w-2.5 rounded-full bg-[#2C5FB8]" data-id="element-347" data-mp="21ggokkmsrjiu" />Soft-ball track</li>
        <li className="flex items-center gap-1.5" data-id="element-348" data-mp="1wg2s7zuk5y0n"><span className="h-2.5 w-2.5 rounded-full bg-cherry" data-id="element-349" data-mp="2aesojd9jztny" />Hard-ball track</li>
        <li className="flex items-center gap-1.5" data-id="element-350" data-mp="2acpt2dpmt8bu"><span className="h-0 w-0 border-x-[5px] border-b-[9px] border-x-transparent border-b-cone" data-id="element-351" data-mp="25h0mvrj3wt6k" />Cone</li>
      </ul>
    </div>;
}